import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Imovel } from '@/integrations/supabase/types';
import { MOCK_FEATURED_PROPERTIES } from '@/data/mockData';

const USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_DATA === 'true';

export interface ImoveisFilters {
  tipo?: string[];
  finalidade?: 'locacao' | 'venda';
  categoria?: 'residencial' | 'comercial' | 'rural';
  padrao?: 'alto_padrao' | 'medio_padrao' | 'padrao' | 'economico';
  valor_min?: number;
  
  // Características mínimas
  quartos_min?: number;
  suites_min?: number;
  banheiros_min?: number;
  vagas_min?: number;
  
  // Área
  area_min?: number;
  area_max?: number;
  
  // Valores
  valor_max?: number;
  
  // Localização
  bairros?: string[];
  search?: string;
  
  // Diferenciais
  diferenciais?: string[];
  diferenciais_condominio?: string[];
}

export interface UseImoveisOptions {
  imobiliariaId?: string;
  filters?: ImoveisFilters;
  limit?: number;
  orderBy?: { column: string; ascending: boolean };
  exclude?: string[]; // IDs de imóveis a excluir
}

/**
 * Hook para buscar imóveis ativos da imobiliária
 */
export function useImoveis(options: UseImoveisOptions = {}) {
  const { imobiliariaId, filters, limit, orderBy, exclude } = options;

  return useQuery({
    queryKey: ['imoveis', imobiliariaId, filters, limit, orderBy, exclude],
    queryFn: async (): Promise<Imovel[]> => {
      // Modo mock: retorna dados mockados convertidos para formato Imovel
      if (USE_MOCK_DATA) {
        // Converte PropertyCardProps para Imovel (estrutura completa)
        const mockImoveis: Imovel[] = MOCK_FEATURED_PROPERTIES.slice(0, limit || 16).map((prop) => {
          const valorNumerico = parseFloat(prop.price.replace(/[^\d]/g, ''));
          
          return {
            id: String(prop.id),
            imobiliaria_id: 'mock-imobiliaria-id',
            codigo: prop.code || `MOCK${prop.id}`,
            titulo: prop.title,
            descricao: null,
            tipo: (prop.type?.toLowerCase() || 'apartamento') as Imovel['tipo'],
            finalidade: prop.saleType === 'Locação' ? ['locacao'] : ['venda'],
            categoria: 'residencial' as const,
            status: 'disponivel' as const,
            anuncio_ativo: true,
            valor_venda: prop.saleType === 'Venda' ? valorNumerico : null,
            valor_locacao: prop.saleType === 'Locação' ? valorNumerico : null,
            valor_condominio: null,
            valor_iptu: null,
            quartos: null,
            suites: null,
            banheiros: null,
            vagas: null,
            area: null,
            area_util: null,
            endereco_completo: prop.address,
            endereco_logradouro: prop.address.split(',')[0] || '',
            endereco_numero: 'S/N',
            endereco_complemento: null,
            endereco_bairro: prop.address.split('-')[1]?.trim().split(',')[0] || '',
            endereco_cidade: 'Campinas',
            endereco_uf: 'SP',
            endereco_cep: '00000-000',
            latitude: null,
            longitude: null,
            mostrar_no_mapa: false,
            fotos: prop.image ? [{ id: '1', url: Array.isArray(prop.image) ? prop.image[0] : prop.image }] : null,
            videos: null,
            tour_virtual: null,
            diferenciais: null,
            diferenciais_condominio: null,
            nome_condominio: null,
            metricas_visualizacoes: prop.engagementCount || 0,
            metricas_leads: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
        });
        
        return mockImoveis;
      }

      // Modo real: busca da view pública do HouseBit
      // A view já filtra apenas imóveis ativos e disponíveis
      let query = supabase
        .from('imoveis_public')
        .select('*');

      // Filtro por imobiliária
      if (imobiliariaId) {
        query = query.eq('imobiliaria_id', imobiliariaId);
      }

      // Filtro por tipo
      if (filters?.tipo && filters.tipo.length > 0) {
        query = query.in('tipo', filters.tipo);
      }

      // Filtro por padrão (apenas residenciais)
      if (filters?.padrao) {
        query = query.eq('padrao', filters.padrao);
      }

      // Filtro por valor mínimo
      if (filters?.valor_min) {
        query = query.or(`valor_venda.gte.${filters.valor_min},valor_locacao.gte.${filters.valor_min}`);
      }

      // Filtro por finalidade
      if (filters?.finalidade) {
        query = query.contains('finalidade', [filters.finalidade]);
      }

      // Filtro por quartos mínimos
      if (filters?.quartos_min) {
        query = query.gte('quartos', filters.quartos_min);
      }

      // Filtro por categoria (residencial/comercial/rural)
      if (filters?.categoria) {
        query = query.eq('categoria', filters.categoria);
      }

      // Filtro por suítes mínimas
      if (filters?.suites_min) {
        query = query.gte('suites', filters.suites_min);
      }

      // Filtro por banheiros mínimos
      if (filters?.banheiros_min) {
        query = query.gte('banheiros', filters.banheiros_min);
      }

      // Filtro por vagas mínimas
      if (filters?.vagas_min) {
        query = query.gte('vagas', filters.vagas_min);
      }

      // Filtro por área (range)
      if (filters?.area_min) {
        query = query.gte('area', filters.area_min);
      }
      if (filters?.area_max) {
        query = query.lte('area', filters.area_max);
      }

      // Filtro por diferenciais do imóvel
      if (filters?.diferenciais && filters.diferenciais.length > 0) {
        query = query.overlaps('diferenciais', filters.diferenciais);
      }

      // Filtro por diferenciais do condomínio
      if (filters?.diferenciais_condominio && filters.diferenciais_condominio.length > 0) {
        query = query.overlaps('diferenciais_condominio', filters.diferenciais_condominio);
      }

      // Filtro por valor máximo
      if (filters?.valor_max) {
        if (filters.finalidade === 'locacao') {
          query = query.lte('valor_locacao', filters.valor_max);
        } else if (filters.finalidade === 'venda') {
          query = query.lte('valor_venda', filters.valor_max);
        }
      }

      // Filtro por bairros
      if (filters?.bairros && filters.bairros.length > 0) {
        query = query.in('endereco_bairro', filters.bairros);
      }

      // Busca textual ampliada (título, endereço completo, cidade, bairro, logradouro)
      if (filters?.search) {
        query = query.or(
          `titulo.ilike.%${filters.search}%,` +
          `endereco_completo.ilike.%${filters.search}%,` +
          `endereco_cidade.ilike.%${filters.search}%,` +
          `endereco_bairro.ilike.%${filters.search}%,` +
          `endereco_logradouro.ilike.%${filters.search}%`
        );
      }

      // Excluir imóveis específicos
      if (exclude && exclude.length > 0) {
        exclude.forEach(id => {
          query = query.neq('id', id);
        });
      }

      // Ordenação
      if (orderBy) {
        query = query.order(orderBy.column, { ascending: orderBy.ascending });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      // Limite de resultados
      if (limit) {
        query = query.limit(limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    },
    enabled: !!imobiliariaId,
    staleTime: 1000 * 60 * 2, // Cache por 2 minutos
  });
}

/**
 * Hook para buscar um único imóvel por código
 */
export function useImovel(codigo?: string) {
  return useQuery({
    queryKey: ['imovel', codigo],
    queryFn: async (): Promise<Imovel | null> => {
      if (!codigo) return null;

      console.log('🔍 Buscando imóvel por código:', codigo);

      // Modo mock: retorna dados mockados convertidos para formato Imovel
      if (USE_MOCK_DATA) {
        const mockProperty = MOCK_FEATURED_PROPERTIES.find(p => p.code === codigo);
        if (!mockProperty) {
          console.log('❌ Imóvel não encontrado no mock');
          return null;
        }
        
        const valorNumerico = parseFloat(mockProperty.price.replace(/[^\d]/g, ''));
        
        return {
          id: String(mockProperty.id),
          imobiliaria_id: 'mock-imobiliaria-id',
          codigo: mockProperty.code || `MOCK${mockProperty.id}`,
          titulo: mockProperty.title,
          descricao: null,
          tipo: (mockProperty.type?.toLowerCase() || 'apartamento') as Imovel['tipo'],
          finalidade: mockProperty.saleType === 'Locação' ? ['locacao'] : ['venda'],
          categoria: 'residencial' as const,
          status: 'disponivel' as const,
          anuncio_ativo: true,
          valor_venda: mockProperty.saleType === 'Venda' ? valorNumerico : null,
          valor_locacao: mockProperty.saleType === 'Locação' ? valorNumerico : null,
          valor_condominio: null,
          valor_iptu: null,
          quartos: null,
          suites: null,
          banheiros: null,
          vagas: null,
          area: null,
          area_util: null,
          endereco_completo: mockProperty.address,
          endereco_logradouro: mockProperty.address.split(',')[0] || '',
          endereco_numero: 'S/N',
          endereco_complemento: null,
          endereco_bairro: mockProperty.address.split('-')[1]?.trim().split(',')[0] || '',
          endereco_cidade: 'Campinas',
          endereco_uf: 'SP',
          endereco_cep: '00000-000',
          latitude: null,
          longitude: null,
          mostrar_no_mapa: false,
          fotos: mockProperty.image ? [{ id: '1', url: Array.isArray(mockProperty.image) ? mockProperty.image[0] : mockProperty.image }] : null,
          videos: null,
          tour_virtual: null,
          diferenciais: null,
          diferenciais_condominio: null,
          nome_condominio: null,
          metricas_visualizacoes: mockProperty.engagementCount || 0,
          metricas_leads: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
      }

      // Buscar por código na view pública do HouseBit
      const { data, error } = await supabase
        .from('imoveis_public')
        .select('*')
        .eq('codigo', codigo)
        .maybeSingle();

      if (error) {
        console.error('❌ Erro ao buscar imóvel:', error);
        throw error;
      }

      if (!data) {
        console.log('❌ Imóvel não encontrado');
        return null;
      }

      const imovel = data as Imovel;
      console.log('✅ Imóvel encontrado:', imovel.titulo);
      return imovel;
    },
    enabled: !!codigo,
    staleTime: 1000 * 60 * 5,
  });
}
